import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { Search, ShoppingBag, Mail, Phone } from 'lucide-react';

const S = {
  heading: { fontSize: 26, fontWeight: 300, letterSpacing: '0.06em', color: '#f5f0e8', marginBottom: 4 },
  sub: { fontSize: 13, color: 'rgba(245,240,232,0.35)', marginBottom: 28, letterSpacing: '0.03em' },
  section: { background: '#13110d', border: '1px solid rgba(186,130,69,0.1)', borderRadius: 10, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { fontSize: 9, letterSpacing: '0.35em', color: 'rgba(245,240,232,0.25)', textTransform: 'uppercase', padding: '12px 20px', textAlign: 'left', borderBottom: '1px solid rgba(186,130,69,0.06)' },
  td: { padding: '14px 20px', borderBottom: '1px solid rgba(186,130,69,0.05)', fontSize: 14, color: '#f5f0e8', verticalAlign: 'middle' },
};

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      // Aggregate orders by email
      const { data: orders } = await supabase
        .from('orders')
        .select('email, phone, total_amount, status, created_at')
        .order('created_at', { ascending: false });

      if (!orders) { setLoading(false); return; }

      // Group by email
      const map = {};
      for (const o of orders) {
        const email = o.email || 'guest';
        if (!map[email]) {
          map[email] = { email, phone: o.phone, orderCount: 0, totalSpent: 0, lastOrder: o.created_at, statuses: [] };
        }
        map[email].orderCount++;
        if (['paid', 'processing', 'shipped', 'delivered'].includes(o.status)) {
          map[email].totalSpent += parseFloat(o.total_amount || 0);
        }
        map[email].statuses.push(o.status);
        if (new Date(o.created_at) > new Date(map[email].lastOrder)) {
          map[email].lastOrder = o.created_at;
          map[email].phone = o.phone || map[email].phone;
        }
      }

      const list = Object.values(map).sort((a, b) => b.totalSpent - a.totalSpent);
      setCustomers(list);
      setLoading(false);
    };
    load();
  }, []);

  const filtered = customers.filter(c =>
    c.email.toLowerCase().includes(search.toLowerCase()) ||
    (c.phone || '').includes(search)
  );

  return (
    <div style={{ color: '#f5f0e8' }}>
      <div style={S.heading}>Customers</div>
      <div style={S.sub}>All buyers from order history · {customers.length} unique customers</div>

      {/* Stats Row */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Customers', value: customers.length, color: '186,130,69' },
          { label: 'Repeat Buyers', value: customers.filter(c => c.orderCount > 1).length, color: '59,130,246' },
          { label: 'Total Revenue', value: `₹${customers.reduce((s, c) => s + c.totalSpent, 0).toLocaleString('en-IN')}`, color: '34,197,94' },
        ].map(stat => (
          <div key={stat.label} style={{
            background: '#13110d', border: `1px solid rgba(${stat.color},0.15)`,
            borderRadius: 10, padding: '16px 20px', minWidth: 150,
            borderTop: `2px solid rgba(${stat.color},0.5)`,
          }}>
            <div style={{ fontSize: 9, letterSpacing: '0.35em', color: 'rgba(245,240,232,0.3)', textTransform: 'uppercase', marginBottom: 8 }}>{stat.label}</div>
            <div style={{ fontSize: 24, fontWeight: 300, color: '#f5f0e8' }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16, position: 'relative', display: 'inline-block' }}>
        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(245,240,232,0.25)' }} />
        <input
          style={{
            background: '#13110d', border: '1px solid rgba(186,130,69,0.15)',
            borderRadius: 6, padding: '10px 14px 10px 34px', color: '#f5f0e8',
            fontSize: 14, outline: 'none', width: 280,
            fontFamily: "'Cormorant Garamond', Georgia, serif",
          }}
          placeholder="Search by email or phone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div style={S.section}>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Customer', 'Phone', 'Orders', 'Total Spent', 'Last Order', 'Top Status'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', color: 'rgba(245,240,232,0.2)' }}>Loading...</td></tr>
              ) : filtered.map((c, i) => {
                // Get most common status
                const statusCounts = c.statuses.reduce((acc, s) => { acc[s] = (acc[s] || 0) + 1; return acc; }, {});
                const topStatus = Object.entries(statusCounts).sort((a, b) => b[1] - a[1])[0]?.[0];

                return (
                  <tr key={c.email}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(186,130,69,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={S.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: 'rgba(186,130,69,0.15)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#ba8245', fontSize: 13, fontWeight: 500, flexShrink: 0,
                        }}>
                          {(c.email[0] || '?').toUpperCase()}
                        </div>
                        <span style={{ fontSize: 13, color: 'rgba(245,240,232,0.8)' }}>{c.email}</span>
                      </div>
                    </td>
                    <td style={{ ...S.td, color: 'rgba(245,240,232,0.5)', fontSize: 13 }}>{c.phone || '—'}</td>
                    <td style={{ ...S.td }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: 'rgba(186,130,69,0.08)', borderRadius: 20,
                        padding: '2px 8px', color: '#ba8245', fontSize: 12,
                      }}>
                        <ShoppingBag size={10} /> {c.orderCount}
                      </span>
                    </td>
                    <td style={{ ...S.td, color: c.totalSpent > 0 ? '#86efac' : 'rgba(245,240,232,0.4)', fontWeight: c.totalSpent > 0 ? 500 : 400 }}>
                      ₹{c.totalSpent.toLocaleString('en-IN')}
                    </td>
                    <td style={{ ...S.td, fontSize: 12, color: 'rgba(245,240,232,0.4)' }}>
                      {new Date(c.lastOrder).toLocaleDateString('en-IN')}
                    </td>
                    <td style={S.td}>
                      <span style={{
                        background: 'rgba(186,130,69,0.08)', border: '1px solid rgba(186,130,69,0.15)',
                        borderRadius: 20, padding: '3px 10px', fontSize: 11,
                        color: 'rgba(245,240,232,0.5)', textTransform: 'capitalize',
                      }}>
                        {topStatus || '—'}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', color: 'rgba(245,240,232,0.2)' }}>No customers found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminCustomers;
